import { useDisclosure } from '@mantine/hooks';
import {
  Box, Button, Flex, Input, InputDescription, InputGroup, InputLabel, Switch, Text
} from '@huspy/briks-web';
import { BaseSlido } from '@modules/core/components/BaseSlido';
import { Pencil } from '@huspy/briks-icons';
import React, { useEffect, useMemo } from 'react';
import {
  Divider, Group, Stack
} from '@mantine/core';
import briksTheme from '@shared/briks-theme';
import { ProposalCreationConfiguratorRoute } from '@modules/proposals/presentation/routes';
import useGetProposalById from '@modules/proposals/api/query/useGetProposalById';
import { USER_EVENTS } from '@shared/analytics/events';
import { css } from '@huspy/styled-system/css';
import { ProposalTemplate } from '@huspy/forge/templates';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import useUpdateProposalConfiguration from '@modules/proposals/api/mutation/useUpdateProposalConfiguration';
import { type ProposalConfigFormValues, useProposalConfigFormContext } from '../..';
import { mapConfiguratorFormValuesToDTO } from '../../mappers/mapConfiguratorFormValuesToDto';
import { mapProposalEntityToTemplateData } from '../../mappers/mapProposalEntityToTemplateData';

const boxStyles = css({
  borderRadius: '24px',
  overflow: 'hidden',
  boxShadow: briksTheme.shadow['shadow-1'],
  border: '1px solid rgba(24, 26, 24, 0.01)',
  backgroundColor: '#fff',
});

type FeesToShow = React.ComponentProps<typeof ProposalTemplate>['config']['feesToShow'];

export const ProposalConfigurationSlido = () => {
  const [opened, { open, close }] = useDisclosure(false);
  const { id } = ProposalCreationConfiguratorRoute.useParams();
  const { data: rawData, isLoading } = useGetProposalById(id);
  const { data } = useGetProposalById(id, mapProposalEntityToTemplateData);
  const { mutateAsync, isPending } = useUpdateProposalConfiguration(id);

  const form = useProposalConfigFormContext();

  const handleSubmit = async (formData: ProposalConfigFormValues) => {
    await mutateAsync(mapConfiguratorFormValuesToDTO(formData));
    trackAmplitudeEvent(USER_EVENTS.PROPOSAL.PROPOSAL_REVISED, {
      proposal_id: rawData?.external_id,
      client_id: rawData?.client_external_id,
    });
    close();
  };
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (!Number.isNaN(value) && +value <= 1_000_000) {
      form.setFieldValue('consultationFee', +value);
    }
  };

  const handleSwitchGroupOnChange = (val: FeesToShow[number]) => {
    const values = [...form.values.config.feesToShow];
    if (form.values.config.feesToShow.includes(val)) {
      // eslint-disable-next-line sonarjs/no-duplicate-string
      form.setFieldValue('config.feesToShow', values.filter((v) => v !== val));
    } else {
      form.setFieldValue('config.feesToShow', [
        ...form.values.config.feesToShow,
        val
      ]);
    }
  };

  const disabled = isLoading;

  const showFeesOptions = useMemo(() => [
    {
      key: 'real_estate_fee',
      text: 'Real Estate Fee',
    },
    {
      key: 'land_department_transfer_fee',
      text: 'Land Department Fee',
    },
    {
      key: 'mortgage_registration_fee',
      text: 'Mortgage Registration Fee',
    },
    {
      key: 'mortgage_release_fee',
      text: 'Mortgage Release Fee',
    },
    ...(rawData?.property_location === 'DIFC' ? [] : [
      {
        key: 'transfer_center_fee',
        text: 'Transfer Center Fee',
      }
    ])
  ], [rawData?.property_location]);

  const customizationOptions = useMemo(() => [
    // Moved to a first step for now
    // {
    //   key: 'showFeeFinancing',
    //   text: 'Show fee financing',
    // },
    {
      key: 'showBankLogos',
      text: 'Bank Logos',
    },
    {
      key: 'showRequiredDocuments',
      text: 'Show required documents',
    }
  ], []);

  const showFeeOptionsValues = showFeesOptions.map(({ key }) => key);
  const isShowAllAdditionalFeesChecked = form.values.config.feesToShow.length === showFeeOptionsValues.length;

  useEffect(() => {
    if (data) {
      // If location was not DIFC and user selects transfer_center_fee,
      // when updating to is DIFC, it will keep the transfer_center_fee
      // value. This line makes sure transfer_center_fee is not present
      // if DIFC
      const feesToShow = data.proposal.propertyDetails.location === 'DIFC'
        ? data.config.feesToShow.filter((f) => f !== 'transfer_center_fee')
        : data.config.feesToShow;

      const values = {
        config: {
          ...data.config,
          feesToShow,
        },
        consultationFee: data.proposal.mortgageDetails.consultationFee,
      };

      form.setInitialValues(values);
      form.setValues(values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <>
      <form onSubmit={ form.onSubmit(handleSubmit) }>
        <BaseSlido
          opened={ opened }
          close={ close }
          title={ (
            <Flex direction='column' gap='2' p='6' pb='0'>
              <Text size='5xl'>
                Customize proposal
              </Text>
            </Flex>
          ) }
          footer={ (
            <Flex justify='end' gap='2'>
              <Button
                type='submit'
                disabled={ isPending }
                loading={ isPending }
                variant={ isPending ? 'secondary' : 'primary' }
                data-test='save-btn'
                onClick={ () => handleSubmit(form.values) }
              >
                Done
              </Button>
            </Flex>
          ) }
        >
          <Flex h='100%'>
            <Box
              w='100%'
              className={ boxStyles }
              mt={ briksTheme.spacing[6] }
              style={ {
                padding: briksTheme.spacing[6],
                transition: 'margin-top 0s ease',
                overflowY: 'auto',
              } }
            >
              <Text size='xl' weight='medium' color='text-secondary'>Proposal Options</Text>
              <Stack mt={ briksTheme.spacing[6] } gap={ briksTheme.spacing[6] }>
                <Text size='sm' weight='semiBold' color='text-secondary'>Proposal customization</Text>
                {customizationOptions.map(({ key, text }) => (
                  <Group grow align='center' key={ key }>
                    <Text size='sm' weight='regular' color='text-placeholder'>{text}</Text>
                    <Flex justify='flex-end'>
                      <Switch
                        disabled={ disabled }
                        checked={ !!form.values.config[key as keyof ProposalConfigFormValues['config']] }
                        onChange={ () => {
                          form.setFieldValue(
                            `config.${key}`,
                            !form.values.config[key as keyof ProposalConfigFormValues['config']]
                          );
                        } }
                      />
                    </Flex>
                  </Group>
                ))}
                <Divider h={ 1 } color='#E5E7EB' bg='#E5E7EB' />
              </Stack>
              <Stack mt={ briksTheme.spacing[6] } gap={ briksTheme.spacing[6] }>
                <Text size='sm' weight='semiBold' color='text-secondary'>Additional fees</Text>
                <Group grow align='center'>
                  <Text size='sm' weight='regular' color='text-placeholder'>Show all additional fees in the proposal</Text>
                  <Flex justify='flex-end'>
                    <Switch
                      disabled={ disabled }
                      checked={ isShowAllAdditionalFeesChecked }
                      onChange={ () => form.setFieldValue(
                        'config.feesToShow',
                        isShowAllAdditionalFeesChecked ? [] : showFeeOptionsValues as FeesToShow
                      ) }
                    />
                  </Flex>
                </Group>
                <Stack gap={ briksTheme.spacing[6] }>
                  {showFeesOptions.map(({ key, text }) => (
                    <Group grow align='center' key={ key }>
                      <Text size='sm' weight='regular' color='text-placeholder'>{text}</Text>
                      <Flex justify='flex-end'>
                        <Switch
                          disabled={ disabled }
                          value={ key }
                          checked={ form.values.config.feesToShow.includes(key as FeesToShow[number]) }
                          onChange={ (e) => {
                          // @ts-expect-error
                            handleSwitchGroupOnChange(e.target.value);
                          } }
                        />
                      </Flex>
                    </Group>
                  ))}
                </Stack>
                <Divider h={ 1 } color='#E5E7EB' bg='#E5E7EB' />
                <Stack gap={ briksTheme.spacing[6] }>
                  <Text size='sm' weight='semiBold' color='text-secondary'>Broker Fee</Text>
                  <InputGroup>
                    <InputLabel>
                      Mortgage Broker Fee (inclusive of VAT)
                    </InputLabel>
                    <Input
                      placeholder='300,000'
                      icon={ <Text size='md' color='text-tertiary' weight='regular'>AED</Text> }
                      type='text'
                      disabled={ disabled }
                      value={ form.values.consultationFee }
                      onChange={ handleInputChange }
                      size='sm'
                      data-test='input-broker-fee'
                    />
                    <InputDescription>
                      Optional
                    </InputDescription>
                  </InputGroup>
                </Stack>
              </Stack>
            </Box>
          </Flex>
        </BaseSlido>
      </form>
      {!opened && (
        <Button
          onClick={ open }
          position='fixed'
          variant='outline'
          bg='base.white'
          borderRadius='full'
          zIndex={ 1001 }
          bottom='10'
          left='50%'
          transform='translateX(60%)'
          shadow='shadow-4'
          data-test='customize-proposal-btn'
        >
          <Pencil width={ 24 } color='inherit' />
          Customize Proposal
        </Button>
      )}
    </>
  );
};
